/**
 * Motor de la sesión activa.
 *
 * Funciones PURAS: reciben una sesión y devuelven otra. No leen ni escriben
 * nada. La persistencia decide cuándo guardar; estas reglas solo dicen cómo
 * queda la sesión después de cada acción.
 *
 * El temporizador es una SUMA DE SEGMENTOS, nunca un contador decreciente:
 *
 *     transcurrido = segundosAcumulados + (ahora − corriendoDesde)
 *
 * Es lo que permite salir 25 minutos a ChatGPT Voice, que Android mate el
 * proceso y que al volver el tiempo siga siendo correcto.
 */

import type { PasoRegistrado, SesionRegistrada, EstadoSesion } from '../modelo/sesion.js';

// ── Temporizador ───────────────────────────────────────────────────────────

/** Segundos transcurridos en un paso, calculados desde los timestamps. */
export function segundosDePaso(paso: PasoRegistrado, ahora: Date = new Date()): number {
  const enCurso = paso.corriendoDesde
    ? Math.max(0, (ahora.getTime() - new Date(paso.corriendoDesde).getTime()) / 1000)
    : 0;
  return Math.floor(paso.segundosAcumulados + enCurso);
}

/** ¿El temporizador de este paso está corriendo ahora mismo? */
export function temporizadorCorriendo(paso: PasoRegistrado): boolean {
  return Boolean(paso.corriendoDesde);
}

/**
 * Segundos que quedan del tiempo SUGERIDO. Puede ser negativo.
 *
 * Negativo significa «tiempo sugerido superado», no un error: el tiempo nunca
 * obliga y el usuario siempre puede seguir o avanzar.
 */
export function segundosRestantes(
  paso: PasoRegistrado,
  minutosSugeridos: number | null,
  ahora: Date = new Date(),
): number | null {
  if (minutosSugeridos === null) return null;
  return minutosSugeridos * 60 - segundosDePaso(paso, ahora);
}

/** Tiempo total de la sesión: la suma de sus pasos. */
export function segundosDeSesion(sesion: SesionRegistrada, ahora: Date = new Date()): number {
  return sesion.pasos.reduce((total, paso) => total + segundosDePaso(paso, ahora), 0);
}

// ── Transformaciones de paso ───────────────────────────────────────────────

function mapearPaso(
  sesion: SesionRegistrada,
  indice: number,
  transformar: (paso: PasoRegistrado) => PasoRegistrado,
): SesionRegistrada {
  const pasos = sesion.pasos.map((paso, i) => (i === indice ? transformar(paso) : paso));
  return { ...sesion, pasos };
}

/** Cierra el segmento en curso, si lo hay, acumulando su tiempo. */
function pausar(paso: PasoRegistrado, ahora: Date): PasoRegistrado {
  if (!paso.corriendoDesde) return paso;
  const transcurrido = Math.max(
    0,
    (ahora.getTime() - new Date(paso.corriendoDesde).getTime()) / 1000,
  );
  return {
    ...paso,
    segundosAcumulados: paso.segundosAcumulados + transcurrido,
    corriendoDesde: null,
  };
}

/** Arranca o reanuda el temporizador del paso. */
export function iniciarTemporizador(
  sesion: SesionRegistrada,
  indice: number,
  ahora: Date = new Date(),
): SesionRegistrada {
  return mapearPaso(sesion, indice, (paso) =>
    paso.corriendoDesde
      ? paso // ya corría: no reiniciar el segmento, sería perder tiempo contado
      : {
          ...paso,
          corriendoDesde: ahora.toISOString(),
          iniciadoEn: paso.iniciadoEn ?? ahora.toISOString(),
        },
  );
}

export function pausarTemporizador(
  sesion: SesionRegistrada,
  indice: number,
  ahora: Date = new Date(),
): SesionRegistrada {
  return mapearPaso(sesion, indice, (paso) => pausar(paso, ahora));
}

/** Vuelve el paso a cero. El tiempo contado se descarta a propósito. */
export function reiniciarTemporizador(
  sesion: SesionRegistrada,
  indice: number,
): SesionRegistrada {
  return mapearPaso(sesion, indice, (paso) => ({
    ...paso,
    segundosAcumulados: 0,
    corriendoDesde: null,
  }));
}

/** Marca la entrada al paso sin arrancar el temporizador. */
export function entrarEnPaso(
  sesion: SesionRegistrada,
  indice: number,
  ahora: Date = new Date(),
): SesionRegistrada {
  const conMarca = mapearPaso(sesion, indice, (paso) => ({
    ...paso,
    iniciadoEn: paso.iniciadoEn ?? ahora.toISOString(),
  }));
  return { ...conMarca, pasoActual: indice };
}

/**
 * Completa el paso actual y avanza.
 *
 * Pausa el temporizador para no seguir contando tiempo de un paso terminado.
 */
export function completarPaso(
  sesion: SesionRegistrada,
  indice: number,
  ahora: Date = new Date(),
): SesionRegistrada {
  const actualizada = mapearPaso(sesion, indice, (paso) => ({
    ...pausar(paso, ahora),
    completado: true,
    omitido: false,
  }));
  const siguiente = Math.min(indice + 1, sesion.pasos.length - 1);
  // Entrar en el siguiente paso forma parte de avanzar: así el momento de
  // entrada queda registrado en la MISMA escritura, sin efectos posteriores.
  return entrarEnPaso(actualizada, siguiente, ahora);
}

/**
 * Omite el paso actual y avanza.
 *
 * Un paso omitido NO cuenta como completado (§11). El motivo es opcional.
 */
export function omitirPaso(
  sesion: SesionRegistrada,
  indice: number,
  motivo?: string,
  ahora: Date = new Date(),
): SesionRegistrada {
  const limpio = motivo?.trim();
  const actualizada = mapearPaso(sesion, indice, (paso) => {
    const pausado = pausar(paso, ahora);
    const siguiente: PasoRegistrado = {
      ...pausado,
      omitido: true,
      completado: false,
    };
    if (limpio) siguiente.motivoOmision = limpio;
    else delete siguiente.motivoOmision;
    return siguiente;
  });
  const siguiente = Math.min(indice + 1, sesion.pasos.length - 1);
  return entrarEnPaso(actualizada, siguiente, ahora);
}

/** Vuelve al paso anterior. Pausa el actual para no contar tiempo de más. */
export function retrocederPaso(
  sesion: SesionRegistrada,
  ahora: Date = new Date(),
): SesionRegistrada {
  if (sesion.pasoActual === 0) return sesion;
  const pausada = pausarTemporizador(sesion, sesion.pasoActual, ahora);
  return entrarEnPaso(pausada, sesion.pasoActual - 1, ahora);
}

/** Guarda las notas de un paso. */
export function anotarPaso(
  sesion: SesionRegistrada,
  indice: number,
  notas: string,
): SesionRegistrada {
  return mapearPaso(sesion, indice, (paso) => ({ ...paso, notas }));
}

// ── Cierre ─────────────────────────────────────────────────────────────────

export interface ResumenSesion {
  completados: number;
  omitidos: number;
  sinTocar: number;
  total: number;
  segundos: number;
  estadoPropuesto: EstadoSesion;
}

/**
 * Estado final que corresponde a la sesión según lo que realmente se hizo.
 *
 * NUNCA se marca como completado lo que se omitió (§12):
 *   - todos los pasos completados        → `completada`
 *   - algo completado, algo no           → `parcial`
 *   - nada completado                    → `abandonada`
 */
export function estadoFinalDe(sesion: SesionRegistrada): EstadoSesion {
  const completados = sesion.pasos.filter((p) => p.completado).length;
  if (completados === sesion.pasos.length && completados > 0) return 'completada';
  if (completados > 0) return 'parcial';
  return 'abandonada';
}

export function resumirSesion(
  sesion: SesionRegistrada,
  ahora: Date = new Date(),
): ResumenSesion {
  const completados = sesion.pasos.filter((p) => p.completado).length;
  const omitidos = sesion.pasos.filter((p) => p.omitido).length;
  return {
    completados,
    omitidos,
    sinTocar: sesion.pasos.length - completados - omitidos,
    total: sesion.pasos.length,
    segundos: segundosDeSesion(sesion, ahora),
    estadoPropuesto: estadoFinalDe(sesion),
  };
}

/**
 * Cierra la sesión.
 *
 * Pausa todos los temporizadores, consolida el tiempo real y fija el estado
 * final. Es una sola transformación: la escritura posterior es atómica.
 */
export function cerrarSesion(
  sesion: SesionRegistrada,
  ahora: Date = new Date(),
): SesionRegistrada {
  const pasos = sesion.pasos.map((paso) => pausar(paso, ahora));
  const consolidada: SesionRegistrada = { ...sesion, pasos };
  return {
    ...consolidada,
    estado: estadoFinalDe(consolidada),
    segundosReales: segundosDeSesion(consolidada, ahora),
    completadaEn: ahora.toISOString(),
  };
}

/** ¿Está el usuario en el último paso? */
export function esUltimoPaso(sesion: SesionRegistrada): boolean {
  return sesion.pasoActual >= sesion.pasos.length - 1;
}

/** ¿Quedan pasos sin completar ni omitir? */
export function quedanPasosSinTocar(sesion: SesionRegistrada): boolean {
  return sesion.pasos.some((p) => !p.completado && !p.omitido);
}
